import {
  CustomMetadata,
  FilterMenuItem,
  ImageCustomMetadata,
  CustomMetadataValue,
  CustomMetadataFieldType,
} from "types";
import { CustomMetadataField } from "vl-api/vlSchemas";
import {
  CustomNameStringIcon,
  CustomNameNumberIcon,
  CustomNameEnumIcon,
  GeolocationIcon,
  CustomLinkIcon,
  DateTimeIcon,
  CustomNameStringArrayIcon,
} from "assets/icons/CustomMetadataIcons";
import { formatUnderscoreWithSpace } from "./formatters";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

export const isCustomMetadata = (type: any): type is CustomMetadataField => {
  return (
    typeof type === "object" &&
    type !== null &&
    "field_id" in type &&
    "field_name" in type &&
    "field_type" in type
  );
};

export const getCustomMetadataDisplayName = (name: string): string => {
  return formatUnderscoreWithSpace(name);
};

export const customMetadataTypeIconMap: Record<
  CustomMetadataFieldType,
  JSX.Element
> = {
  string: <CustomNameStringIcon />,
  float: <CustomNameNumberIcon />,
  enum: <CustomNameEnumIcon />,
  datetime: <DateTimeIcon />,
  geolocation: <GeolocationIcon />,
  link: <CustomLinkIcon />,
  string_array: <CustomNameStringArrayIcon />,
};

export const getFormattedCustomMetadataValue = (
  field: ImageCustomMetadata,
): string => {
  const { field_type, value } = field;

  const formatters: Record<
    CustomMetadataFieldType,
    (value: CustomMetadataValue) => string
  > = {
    string: (value) => String(value),
    float: (value) => String(value),
    enum: (value) => (Array.isArray(value) ? value.join(", ") : String(value)),
    link: (value) => (Array.isArray(value) ? value.join(", ") : String(value)),
    datetime: (value) => new Date(value as string | number).toLocaleString(),
    geolocation: (value) =>
      Array.isArray(value) && value.length >= 2
        ? `Lat: ${value[0]}, Lon: ${value[1]}`
        : String(value),
    string_array: (value) =>
      Array.isArray(value) ? value.join(", ") : String(value),
  };

  const formatter = formatters[field_type];
  return formatter ? formatter(value) : String(value);
};

export const createCustomMetadataFilterMenuItem = (
  field: CustomMetadata,
): FilterMenuItem => {
  const { field_type, has_values } = field;
  const icon = customMetadataTypeIconMap[field_type];

  return {
    icon,
    type: field,
    disabled: !has_values,
    entityTypes: ["OBJECTS", "IMAGES"],
  };
};

export const createCustomMetadataFieldFilterMenuItem = (
  field: CustomMetadataField,
): FilterMenuItem => {
  const { field_type } = field;
  const icon = customMetadataTypeIconMap[field_type];

  return {
    icon,
    type: field,
    disabled: false,
    entityTypes: ["OBJECTS", "IMAGES"],
  };
};

// Utility functions for converting between dayjs objects and formatted strings
export const formatDateForAPI = (date: Dayjs | null): string | null =>
  date?.utc().format("YYYY-MM-DDTHH:mm:ss[Z]") || null;

export const formatDateRangeForAPI = (
  dateRange: [Dayjs, Dayjs] | null,
): [string, string] | null =>
  dateRange
    ? [formatDateForAPI(dateRange[0])!, formatDateForAPI(dateRange[1])!]
    : null;

export const parseDateFromAPI = (dateString: string | null): Dayjs | null =>
  dateString ? dayjs(dateString).local() : null;

export const parseDateRangeFromAPI = (
  dateRange: [string, string] | null,
): [Dayjs, Dayjs] | null =>
  dateRange
    ? [parseDateFromAPI(dateRange[0])!, parseDateFromAPI(dateRange[1])!]
    : null;
